/**
 * LiveStopDetectionMap
 * Leaflet map showing gaushala stop markers colored by status and a polyline route path.
 * Rendered inside each expanded ExecutionCard on the LiveTrackingPage.
 */

import { useEffect, useRef } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polyline,
  Tooltip,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { StopLog } from '../../services/transport/api';

// Fix Leaflet default icons (same pattern as MapContainer.tsx)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface LiveStopDetectionMapProps {
  stops: StopLog[];
  mfgUnitLatitude?: number;
  mfgUnitLongitude?: number;
  mfgUnitName?: string;
}

// Status → color mapping
const STATUS_COLORS: Record<string, string> = {
  COMPLETED: '#22c55e',  // green-500
  ARRIVED: '#3b82f6',    // blue-500
  SKIPPED: '#f97316',    // orange-500
  PENDING: '#9ca3af',    // gray-400
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'COMPLETED': return 'Completed';
    case 'ARRIVED': return 'Arrived';
    case 'SKIPPED': return 'Skipped';
    case 'PENDING': return 'Pending';
    default: return status;
  }
};

/** Creates a colored circle divIcon with sequence number */
const createStopIcon = (seq: number, status: string) => {
  const color = STATUS_COLORS[status] || STATUS_COLORS.PENDING;
  return L.divIcon({
    className: '', // override default leaflet-div-icon styling
    html: `<div style="
      width:28px;height:28px;border-radius:50%;
      background:${color};color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:700;font-size:13px;
      border:2px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">${seq}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

/** Factory/destination icon for the manufacturing unit */
const createMfgIcon = () => {
  return L.divIcon({
    className: '',
    html: `<div style="
      width:32px;height:32px;border-radius:6px;
      background:#7c3aed;color:#fff;
      display:flex;align-items:center;justify-content:center;
      font-weight:700;font-size:16px;
      border:2px solid #fff;
      box-shadow:0 2px 6px rgba(0,0,0,0.3);
    ">&#9881;</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
};

/** Auto-fits map bounds to show all markers */
function FitBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (positions.length === 0) return;
    // Only auto-fit on first render or when positions change significantly
    const bounds = L.latLngBounds(positions.map(p => L.latLng(p[0], p[1])));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    fitted.current = true;
  }, [positions.length]); // re-fit only when count changes

  return null;
}

export default function LiveStopDetectionMap({
  stops,
  mfgUnitLatitude,
  mfgUnitLongitude,
  mfgUnitName,
}: LiveStopDetectionMapProps) {
  // Filter stops that have valid coordinates
  const mappableStops = stops
    .filter(s => s.gaushalaLatitude != null && s.gaushalaLongitude != null)
    .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));

  if (mappableStops.length === 0) {
    return (
      <div className="h-[200px] rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
        No stop coordinates available for map display
      </div>
    );
  }

  // Collect all positions for bounds fitting
  const allPositions: [number, number][] = mappableStops.map(s => [
    s.gaushalaLatitude!,
    s.gaushalaLongitude!,
  ]);
  if (mfgUnitLatitude != null && mfgUnitLongitude != null) {
    allPositions.push([mfgUnitLatitude, mfgUnitLongitude]);
  }

  // Build polyline segments with color by status
  const polylinePositions: [number, number][] = mappableStops.map(s => [
    s.gaushalaLatitude!,
    s.gaushalaLongitude!,
  ]);
  if (mfgUnitLatitude != null && mfgUnitLongitude != null) {
    polylinePositions.push([mfgUnitLatitude, mfgUnitLongitude]);
  }

  // Build colored segments: completed segments green, rest gray dashed
  const segments: { positions: [number, number][]; completed: boolean }[] = [];
  for (let i = 0; i < polylinePositions.length - 1; i++) {
    const fromStop = i < mappableStops.length ? mappableStops[i] : null;
    const isCompleted = fromStop
      ? (fromStop.status === 'COMPLETED' || fromStop.statusCode === 'COMPLETED')
      : false;
    segments.push({
      positions: [polylinePositions[i], polylinePositions[i + 1]],
      completed: isCompleted,
    });
  }

  // Center on first stop
  const center: [number, number] = allPositions[0];

  return (
    <div className="mb-4">
      <div className="rounded-lg overflow-hidden border border-gray-200" style={{ height: 350 }}>
        <MapContainer
          center={center}
          zoom={12}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
          scrollWheelZoom={true}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />

          <FitBounds positions={allPositions} />

          {/* Route polyline segments */}
          {segments.map((seg, i) => (
            <Polyline
              key={`seg-${i}`}
              positions={seg.positions}
              pathOptions={{
                color: seg.completed ? '#22c55e' : '#d1d5db',
                weight: 3,
                dashArray: seg.completed ? undefined : '8 6',
                opacity: 0.8,
              }}
            />
          ))}

          {/* Gaushala stop markers */}
          {mappableStops.map((stop) => {
            const status = stop.status || stop.statusCode || 'PENDING';
            const seq = stop.sequenceOrder || 0;
            return (
              <Marker
                key={`stop-${stop.id}`}
                position={[stop.gaushalaLatitude!, stop.gaushalaLongitude!]}
                icon={createStopIcon(seq, status)}
              >
                <Tooltip direction="top" offset={[0, -14]}>
                  <div className="text-xs">
                    <p className="font-semibold">{stop.gaushalaName || `Gaushala ${stop.gaushalaId}`}</p>
                    <p>Status: {statusLabel(status)}</p>
                    {stop.quantityCollectedKg != null && (
                      <p>Collected: {stop.quantityCollectedKg} kg</p>
                    )}
                  </div>
                </Tooltip>
              </Marker>
            );
          })}

          {/* Manufacturing unit marker */}
          {mfgUnitLatitude != null && mfgUnitLongitude != null && (
            <Marker
              position={[mfgUnitLatitude, mfgUnitLongitude]}
              icon={createMfgIcon()}
            >
              <Tooltip direction="top" offset={[0, -16]}>
                <div className="text-xs">
                  <p className="font-semibold">{mfgUnitName || 'Manufacturing Unit'}</p>
                  <p>Destination</p>
                </div>
              </Tooltip>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* Inline legend */}
      <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-600">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.COMPLETED }} />
          Completed
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.ARRIVED }} />
          Arrived
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.SKIPPED }} />
          Skipped
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.PENDING }} />
          Pending
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-4 h-4 rounded" style={{ background: '#7c3aed', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10 }}>&#9881;</span>
          Mfg Unit
        </span>
      </div>
    </div>
  );
}
